import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 思维启发与个人成长 — 复利效应的奇迹
 *
 * 暗金质感 + 指数增长曲线 + 滚动数字 + 故事化案例
 * 叙事弧：
 *   开场惊人事实 → 复利公式拆解 → 三个对比（每天 1% / 0% / -1%） →
 *   时间长度对比（10/20/30 年） → 真实案例（巴菲特/读书/锻炼） →
 *   行动公式 → 终极洞察
 * 尺寸：1920 × 1080
 */

interface ScenarioComparison {
  label: string;
  daily: number;
  emoji: string;
  color: string;
  oneYear: string;
  tenYears: string;
}

interface RealCase {
  who: string;
  daily: string;
  result: string;
  icon: string;
  accent: string;
}

interface ActionFormula {
  step: string;
  hint: string;
  icon: string;
}

interface CompoundEffectMagicProps {
  topic: string;
  subtitle: string;
  hookStat: { number: string; unit: string; description: string };
  formula: { equation: string; explain: string };
  scenarios: ScenarioComparison[];
  growthChartYears: number;
  growthDailyRates: { rate: number; label: string; color: string }[];
  realCases: RealCase[];
  actionFormula: ActionFormula[];
  insight: string;
}

const DEFAULT_PROPS: CompoundEffectMagicProps = {
  topic: '复利效应：时间与坚持的奇迹',
  subtitle: 'The Compound Effect — Why Small Daily Actions Change Everything',
  hookStat: {
    number: '37.78',
    unit: '倍',
    description: '每天进步 1%，一年后你将变强 37.78 倍 — 这就是复利的暴力',
  },
  formula: {
    equation: '(1 + r)ⁿ',
    explain: 'r = 每天进步率 · n = 坚持的天数。看似温柔的数字游戏，藏着改变人生的核武器',
  },
  scenarios: [
    {
      label: '每天进步 1%',
      daily: 1.01,
      emoji: '📈',
      color: '#22c55e',
      oneYear: '× 37.78',
      tenYears: '× 78,500,000,000',
    },
    {
      label: '每天原地踏步',
      daily: 1.0,
      emoji: '😐',
      color: '#94a3b8',
      oneYear: '× 1.00',
      tenYears: '× 1.00',
    },
    {
      label: '每天退步 1%',
      daily: 0.99,
      emoji: '📉',
      color: '#ef4444',
      oneYear: '× 0.026',
      tenYears: '~ 归零',
    },
  ],
  growthChartYears: 10,
  growthDailyRates: [
    { rate: 1.01, label: '每天 +1%', color: '#22c55e' },
    { rate: 1.005, label: '每天 +0.5%', color: '#3b82f6' },
    { rate: 1.001, label: '每天 +0.1%', color: '#a78bfa' },
    { rate: 1.0, label: '每天 0%', color: '#94a3b8' },
    { rate: 0.999, label: '每天 -0.1%', color: '#f59e0b' },
    { rate: 0.99, label: '每天 -1%', color: '#ef4444' },
  ],
  realCases: [
    {
      who: '巴菲特',
      daily: '每天阅读 5 小时 · 坚持 70+ 年',
      result: '5,800 万 → 1,100 亿美元',
      icon: '📚',
      accent: '#fbbf24',
    },
    {
      who: '村上春树',
      daily: '每天写 10 页 + 跑 10 公里 · 40 年',
      result: '50+ 部作品 · 销量过亿',
      icon: '✍️',
      accent: '#a78bfa',
    },
    {
      who: '健身者',
      daily: '每天 30 分钟力量训练 · 5 年',
      result: '体脂 28% → 14% · 肌肉量翻倍',
      icon: '💪',
      accent: '#22c55e',
    },
  ],
  actionFormula: [
    { step: '挑选 1 件极小的事', hint: '小到不可能失败 · 比如读 1 页书', icon: '🎯' },
    { step: '锁定一个固定时间', hint: '与已有习惯绑定 · 早咖啡后 / 睡前', icon: '⏰' },
    { step: '记录连续天数', hint: '可视化进度 · 不破坏链条', icon: '📊' },
    { step: '允许失败但永不停止', hint: '断了就重来 · 不要"反正都断了"', icon: '🔄' },
  ],
  insight:
    '复利的可怕，不在于快——而在于慢。慢到你前 90% 的时间都看不见效果，直到某一天突然发现：你已经站在了山顶。',
};

export const CompoundEffectMagicTemplate: React.FC<
  Partial<CompoundEffectMagicProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const hookStart = 30;
  const formulaStart = 90;
  const scenariosStart = 150;
  const chartStart = 240;
  const casesStart = 360;
  const actionStart = 460;
  const insightStart = 540;
  const totalFrames = 600;

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const hookOpacity = interpolate(frame, [hookStart, hookStart + 25], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const counterValue = interpolate(
    frame,
    [hookStart + 10, hookStart + 60],
    [0, parseFloat(p.hookStat.number)],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const insightOpacity = interpolate(frame, [insightStart, insightStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Build chart line paths
  // Margins reserve space inside the SVG for axis & line labels so nothing overflows
  const chartLeftPad = 56;
  const chartRightPad = 168;
  const chartTopPad = 8;
  const chartBottomPad = 38;
  const chartW = 612;
  const chartH = 240;
  const svgW = chartLeftPad + chartW + chartRightPad; // 836
  const svgH = chartTopPad + chartH + chartBottomPad; // 286
  const days = p.growthChartYears * 365;
  const drawProgress = interpolate(
    frame,
    [chartStart, chartStart + 120],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );
  const visibleDays = Math.floor(days * drawProgress);

  // Calculate max value across all rates for normalization
  const maxValue = p.growthDailyRates.reduce((max, r) => {
    const finalVal = Math.pow(r.rate, days);
    return Math.max(max, finalVal);
  }, 1);
  const yScale = (val: number) => {
    if (val <= 0) return chartH;
    const logVal = Math.log10(val);
    const logMax = Math.log10(maxValue);
    const logMin = -3;
    const norm = (logVal - logMin) / (logMax - logMin);
    return chartH - Math.max(0, Math.min(1, norm)) * chartH;
  };

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 30% 20%, #1a1410 0%, #0a0805 60%, #050302 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: '#f5f5dc',
        overflow: 'hidden',
      }}
    >
      {/* Gold dust particles */}
      {Array.from({ length: 80 }).map((_, i) => {
        const x = (i * 311) % 1920;
        const y = (i * 197) % 1080;
        const drift = Math.sin(frame * 0.02 + i * 0.7) * 12;
        const driftX = Math.cos(frame * 0.018 + i * 0.5) * 8;
        const flicker = 0.15 + 0.5 * Math.abs(Math.sin(frame * 0.04 + i));
        const size = 1 + (i % 4);
        return (
          <div
            key={`dust-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: i % 5 === 0 ? '#fbbf24' : i % 5 === 1 ? '#fde047' : '#f59e0b',
              opacity: flicker * 0.7,
              boxShadow: `0 0 ${size * 5}px currentColor`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 22,
          left: 50,
          right: 50,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 16}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 22,
        }}
      >
        <div
          style={{
            padding: '8px 20px',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            borderRadius: 10,
            fontSize: 18,
            fontWeight: 800,
            color: '#1a1410',
            letterSpacing: '0.06em',
          }}
        >
          ⨋ COMPOUND EFFECT
        </div>
        <h1
          style={{
            fontSize: 44,
            fontWeight: 900,
            margin: 0,
            background: 'linear-gradient(90deg, #fde047, #fbbf24, #f59e0b)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <span
          style={{
            fontSize: 20,
            color: '#a8a29e',
            whiteSpace: 'nowrap',
            fontStyle: 'italic',
            marginLeft: 'auto',
          }}
        >
          {p.subtitle}
        </span>
      </div>

      {/* ===== Hook stat ===== */}
      <div
        style={{
          position: 'absolute',
          top: 150,
          left: 50,
          width: 600,
          padding: '20px 26px',
          background: 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(245,158,11,0.08))',
          border: '1.5px solid rgba(251,191,36,0.3)',
          borderRadius: 16,
          opacity: hookOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
          <span
            style={{
              fontSize: 76,
              fontWeight: 900,
              background: 'linear-gradient(180deg, #fde047, #f59e0b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1,
              fontFamily: 'monospace',
              textShadow: '0 0 30px rgba(251,191,36,0.4)',
            }}
          >
            {counterValue.toFixed(2)}
          </span>
          <span style={{ fontSize: 44, fontWeight: 800, color: '#fbbf24' }}>
            {p.hookStat.unit}
          </span>
        </div>
        <div style={{ fontSize: 24, color: '#fde68a', lineHeight: 1.4, fontWeight: 600 }}>
          {p.hookStat.description}
        </div>
      </div>

      {/* ===== Formula ===== */}
      <div
        style={{
          position: 'absolute',
          top: 150,
          left: 680,
          width: 580,
          padding: '18px 24px',
          background: 'rgba(20,15,10,0.7)',
          border: '1.5px solid rgba(251,191,36,0.2)',
          borderRadius: 16,
          opacity: interpolate(frame, [formulaStart, formulaStart + 25], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 900,
            color: '#fde047',
            fontFamily: '"Times New Roman", Georgia, serif',
            fontStyle: 'italic',
            textAlign: 'center',
            lineHeight: 1,
            marginBottom: 12,
            textShadow: '0 0 24px rgba(251,191,36,0.4)',
          }}
        >
          {p.formula.equation}
        </div>
        <div style={{ fontSize: 24, color: '#d6d3d1', textAlign: 'center', lineHeight: 1.45 }}>
          {p.formula.explain}
        </div>
      </div>

      {/* ===== Three Scenarios (right top) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 96,
          left: 1290,
          width: 580,
          padding: '18px 22px 16px',
          background: 'rgba(20,15,10,0.7)',
          border: '1.5px solid rgba(251,191,36,0.2)',
          borderRadius: 16,
        }}
      >
        <div style={{ fontSize: 24, color: '#fde68a', letterSpacing: '0.1em', marginBottom: 12, fontWeight: 700 }}>
          ⨋ 三种人生 · 一年后
        </div>
        {p.scenarios.map((s, i) => {
          const sIn = spring({
            frame: frame - (scenariosStart + i * 15),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={`sc-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '10px 14px',
                marginBottom: 8,
                background: `linear-gradient(135deg, ${s.color}18, transparent)`,
                borderLeft: `4px solid ${s.color}`,
                borderRadius: '4px 12px 12px 4px',
                opacity: sIn,
                transform: `translateX(${(1 - sIn) * 24}px)`,
              }}
            >
              <div style={{ fontSize: 38, flexShrink: 0, lineHeight: 1 }}>{s.emoji}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: s.color,
                    lineHeight: 1.2,
                    marginBottom: 6,
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 12,
                    flexWrap: 'wrap',
                    rowGap: 4,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 18,
                        color: '#a8a29e',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                      }}
                    >
                      1 年
                    </span>
                    <span
                      style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: '#f5f5dc',
                        fontFamily: 'monospace',
                        lineHeight: 1,
                      }}
                    >
                      {s.oneYear}
                    </span>
                  </div>
                  <div
                    style={{
                      width: 1,
                      height: 18,
                      background: 'rgba(168,162,158,0.3)',
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 18,
                        color: '#a8a29e',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                      }}
                    >
                      10 年
                    </span>
                    <span
                      style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: s.color,
                        fontFamily: 'monospace',
                        lineHeight: 1,
                      }}
                    >
                      {s.tenYears}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Growth Chart ===== */}
      <div
        style={{
          position: 'absolute',
          top: 445,
          left: 50,
          width: 880,
          padding: '12px 22px 10px',
          background: 'rgba(20,15,10,0.7)',
          border: '1.5px solid rgba(251,191,36,0.2)',
          borderRadius: 16,
          opacity: interpolate(frame, [chartStart, chartStart + 25], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 24, color: '#fde68a', letterSpacing: '0.1em', fontWeight: 700 }}>
            📊 {p.growthChartYears} 年增长曲线（对数刻度）
          </div>
        </div>
        <svg
          width="100%"
          height={svgH}
          viewBox={`0 0 ${svgW} ${svgH}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: 'hidden', display: 'block' }}
        >
          <defs>
            <linearGradient id="goldFade" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
            </linearGradient>
          </defs>

          <g transform={`translate(${chartLeftPad}, ${chartTopPad})`}>
            {/* Y-axis log gridlines — sparse ticks every 3 log units to span full range without crowding */}
            {[
              { value: 1e-3, label: '10⁻³' },
              { value: 1, label: '×1' },
              { value: 1e3, label: '1k' },
              { value: 1e6, label: '1M' },
              { value: 1e9, label: '1B' },
              { value: 1e12, label: '1T' },
              { value: 1e15, label: '10¹⁵' },
            ].map(({ value, label }) => {
              const y = yScale(value);
              if (y < -2 || y > chartH + 2) return null;
              return (
                <g key={`grid-${value}`}>
                  <line
                    x1="0"
                    y1={y}
                    x2={chartW}
                    y2={y}
                    stroke="rgba(168,162,158,0.12)"
                    strokeDasharray="2 4"
                  />
                  <text
                    x="-10"
                    y={y + 7}
                    fill="#d6d3d1"
                    fontSize="20"
                    fontWeight="700"
                    textAnchor="end"
                  >
                    {label}
                  </text>
                </g>
              );
            })}

            {/* X-axis year markers */}
            {Array.from({ length: p.growthChartYears + 1 }).map((_, yr) => {
              const x = (yr / p.growthChartYears) * chartW;
              return (
                <g key={`yr-${yr}`}>
                  <line
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={chartH}
                    stroke="rgba(168,162,158,0.08)"
                  />
                  <text
                    x={x}
                    y={chartH + 26}
                    fill="#d6d3d1"
                    fontSize="18"
                    fontWeight="600"
                    textAnchor="middle"
                  >
                    {yr === 0 ? '起点' : `${yr}年`}
                  </text>
                </g>
              );
            })}

            {/* Lines */}
            {p.growthDailyRates.map((r, ri) => {
              const points: string[] = [];
              const sampleEvery = 7;
              const limit = Math.min(visibleDays, days);
              for (let d = 0; d <= limit; d += sampleEvery) {
                const val = Math.pow(r.rate, d);
                const x = (d / days) * chartW;
                const y = yScale(val);
                points.push(`${x},${y}`);
              }
              const finalVal = Math.pow(r.rate, days);
              const showLabel = visibleDays >= days * 0.95;
              return (
                <g key={`line-${ri}`}>
                  <polyline
                    points={points.join(' ')}
                    fill="none"
                    stroke={r.color}
                    strokeWidth="3"
                    opacity={r.rate === 1 ? 0.6 : 0.95}
                    strokeLinejoin="round"
                    filter={r.rate > 1 ? `drop-shadow(0 0 4px ${r.color}80)` : undefined}
                  />
                  {showLabel && (
                    <text
                      x={chartW + 10}
                      y={yScale(finalVal) + 6}
                      fill={r.color}
                      fontSize="20"
                      fontWeight="800"
                    >
                      {r.label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Marker circle on top line current position */}
            {(() => {
              const r = p.growthDailyRates[0];
              const limit = Math.min(visibleDays, days);
              const val = Math.pow(r.rate, limit);
              const x = (limit / days) * chartW;
              const y = yScale(val);
              return (
                <circle
                  cx={x}
                  cy={y}
                  r={6 + Math.sin(frame * 0.15) * 2}
                  fill={r.color}
                  filter={`drop-shadow(0 0 6px ${r.color})`}
                />
              );
            })()}
          </g>
        </svg>
      </div>

      {/* ===== Real Cases ===== */}
      <div
        style={{
          position: 'absolute',
          top: 446,
          left: 950,
          width: 920,
          padding: '14px 22px 16px',
          background: 'rgba(20,15,10,0.7)',
          border: '1.5px solid rgba(251,191,36,0.2)',
          borderRadius: 16,
        }}
      >
        <div style={{ fontSize: 24, color: '#fde68a', letterSpacing: '0.1em', marginBottom: 12, fontWeight: 700 }}>
          🌟 真实案例 · 时间是最大的杠杆
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.realCases.map((c, i) => {
            const sIn = spring({
              frame: frame - (casesStart + i * 14),
              fps,
              config: { damping: 14, stiffness: 100 },
            });
            return (
              <div
                key={`case-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  background: `linear-gradient(135deg, ${c.accent}14, transparent 70%)`,
                  borderLeft: `4px solid ${c.accent}`,
                  borderRadius: '4px 12px 12px 4px',
                  opacity: sIn,
                  transform: `translateX(${(1 - sIn) * 24}px)`,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: `radial-gradient(circle, ${c.accent}66, ${c.accent}14)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 26,
                    flexShrink: 0,
                    border: `1px solid ${c.accent}55`,
                  }}
                >
                  {c.icon}
                </div>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 900,
                    color: c.accent,
                    flexShrink: 0,
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.who}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    color: '#e7e5e4',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    marginLeft: 8,
                  }}
                >
                  {c.daily}
                </div>
                <div
                  style={{
                    fontSize: 24,
                    color: c.accent,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  →
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: c.accent,
                    lineHeight: 1.3,
                    textShadow: `0 0 12px ${c.accent}40`,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {c.result}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Action Formula ===== */}
      <div
        style={{
          position: 'absolute',
          top: 810,
          left: 50,
          right: 50,
          padding: '12px 22px 14px',
          background: 'linear-gradient(135deg, rgba(251,191,36,0.12), rgba(245,158,11,0.06))',
          border: '1.5px solid rgba(251,191,36,0.3)',
          borderRadius: 14,
        }}
      >
        <div style={{ fontSize: 24, color: '#fde68a', letterSpacing: '0.1em', marginBottom: 8, fontWeight: 700 }}>
          🛠 启动你的复利公式 · 4 步法
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {p.actionFormula.map((a, i) => {
            const sIn = spring({
              frame: frame - (actionStart + i * 12),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <React.Fragment key={`act-${i}`}>
                <div
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    background: 'rgba(20,15,10,0.6)',
                    border: '1px solid rgba(251,191,36,0.25)',
                    borderRadius: 12,
                    opacity: sIn,
                    transform: `translateY(${(1 - sIn) * 16}px)`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 2,
                    }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                        color: '#1a1410',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 900,
                        fontSize: 17,
                        flexShrink: 0,
                      }}
                    >
                      0{i + 1}
                    </div>
                    <span style={{ fontSize: 24, flexShrink: 0 }}>{a.icon}</span>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 800,
                        color: '#fde047',
                        lineHeight: 1.2,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {a.step}
                    </div>
                  </div>
                  <div style={{ fontSize: 20, color: '#d6d3d1', lineHeight: 1.4, marginTop: 4 }}>
                    {a.hint}
                  </div>
                </div>
                {i < p.actionFormula.length - 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#fbbf24',
                      fontSize: 22,
                      opacity: sIn,
                    }}
                  >
                    →
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== Insight ===== */}
      <div
        style={{
          position: 'absolute',
          top: 985,
          left: 50,
          right: 50,
          padding: '16px 28px',
          background:
            'linear-gradient(135deg, rgba(251,191,36,0.2), rgba(245,158,11,0.12))',
          border: '1.5px solid rgba(251,191,36,0.4)',
          borderRadius: 14,
          opacity: insightOpacity,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <span style={{ fontSize: 30 }}>💎</span>
        <div
          style={{
            flex: 1,
            fontSize: 22,
            fontWeight: 800,
            color: '#fde68a',
            lineHeight: 1.45,
            textShadow: '0 0 14px rgba(251,191,36,0.4)',
          }}
        >
          {p.insight}
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, totalFrames], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #fbbf24, #f59e0b, #ea580c)',
        }}
      />
    </AbsoluteFill>
  );
};
